import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Store, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  CreditCard,
  Calendar,
  RefreshCw
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

interface Merchant {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  plan: string;
  subscription_status: string;
  trial_ends_at: string | null;
  trial_started_at: string | null;
  billing_cycle_start: string | null;
  splits_this_month: number;
}

interface Props {
  merchants: Merchant[];
  onRefresh: () => void;
}

const plans = [
  { value: 'trial', label: 'Trial', color: 'bg-gray-500/10 text-gray-600' },
  { value: 'starter', label: 'Starter', color: 'bg-blue-500/10 text-blue-600' },
  { value: 'growth', label: 'Growth', color: 'bg-purple-500/10 text-purple-600' },
  { value: 'enterprise', label: 'Enterprise', color: 'bg-orange-500/10 text-orange-600' },
];

const statuses = [
  { value: 'trialing', label: 'Trialing', color: 'bg-blue-500/10 text-blue-600' },
  { value: 'active', label: 'Active', color: 'bg-green-500/10 text-green-600' },
  { value: 'expired', label: 'Expired', color: 'bg-red-500/10 text-red-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-500/10 text-gray-600' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-500/10 text-yellow-600' },
];

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function MerchantSubscriptionManager({ merchants, onRefresh }: Props) {
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const updateMerchant = async (
    merchantId: string, 
    updates: Partial<Pick<Merchant, 'plan' | 'subscription_status' | 'billing_cycle_start'>>
  ) => {
    setUpdatingId(merchantId);
    try {
      // If activating a subscription, set billing cycle start
      const finalUpdates = { ...updates };
      if (updates.subscription_status === 'active' && updates.plan !== 'trial') {
        finalUpdates.billing_cycle_start = new Date().toISOString();
      }

      const { error } = await supabase
        .from('merchants')
        .update(finalUpdates)
        .eq('id', merchantId);

      if (error) throw error;

      toast.success("Subscription updated successfully");
      onRefresh();
    } catch (error) {
      console.error("Error updating merchant:", error);
      toast.error("Failed to update subscription");
    } finally {
      setUpdatingId(null);
    }
  };

  const startTrial = async (merchantId: string) => {
    setUpdatingId(merchantId);
    try {
      // 30 days trial period per business policy
      const trialStart = new Date();
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);

      const { error } = await supabase
        .from('merchants')
        .update({
          plan: 'trial',
          subscription_status: 'trialing',
          trial_started_at: trialStart.toISOString(),
          trial_ends_at: trialEnd.toISOString(),
          splits_this_month: 0,
        })
        .eq('id', merchantId);

      if (error) throw error;

      toast.success("30-day trial started");
      onRefresh();
    } catch (error) {
      console.error("Error starting trial:", error);
      toast.error("Failed to start trial");
    } finally {
      setUpdatingId(null);
    }
  };

  const resetSplitCount = async (merchantId: string) => {
    setUpdatingId(merchantId);
    try {
      const { error } = await supabase
        .from('merchants')
        .update({ 
          splits_this_month: 0,
          billing_cycle_start: new Date().toISOString()
        })
        .eq('id', merchantId);

      if (error) throw error;

      toast.success("Split count reset and new billing cycle started");
      onRefresh();
    } catch (error) {
      console.error("Error resetting splits:", error);
      toast.error("Failed to reset splits");
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const s = statuses.find(st => st.value === status);
    return <Badge className={s?.color || 'bg-gray-500/10 text-gray-600'}>{s?.label || status}</Badge>;
  };

  const getPlanBadge = (plan: string) => {
    const p = plans.find(pl => pl.value === plan);
    return <Badge variant="outline" className={p?.color || ''}>{p?.label || plan}</Badge>;
  };

  const getTrialDaysRemaining = (trialEndsAt: string | null) => {
    if (!trialEndsAt) return null;
    const end = new Date(trialEndsAt);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="space-y-4">
      {merchants.map((merchant) => {
        const trialDays = getTrialDaysRemaining(merchant.trial_ends_at);
        const isTrialExpired = trialDays !== null && trialDays < 0;
        const isUpdating = updatingId === merchant.id;

        return (
          <Card key={merchant.id} className={isUpdating ? 'opacity-50' : ''}>
            <CardContent className="pt-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Merchant Info */}
                <div className="flex items-start gap-3 flex-1">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Store className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{merchant.store_name}</h3>
                      {getPlanBadge(merchant.plan)}
                      {getStatusBadge(merchant.subscription_status)}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{merchant.shopify_store_domain}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      {merchant.subscription_status === 'trialing' && trialDays !== null && (
                        <span className={`flex items-center gap-1 ${isTrialExpired ? 'text-destructive' : ''}`}>
                          <Clock className="h-3 w-3" />
                          {isTrialExpired ? 'Trial expired' : `${trialDays} days left`}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <CreditCard className="h-3 w-3" />
                        {merchant.splits_this_month} splits this month
                      </span>
                      {merchant.billing_cycle_start && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Cycle: {format(new Date(merchant.billing_cycle_start), 'MMM d')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Plan Selector */}
                  <Select
                    value={merchant.plan}
                    onValueChange={(value) => updateMerchant(merchant.id, { plan: value })}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Status Selector */}
                  <Select
                    value={merchant.subscription_status}
                    onValueChange={(value) => updateMerchant(merchant.id, { subscription_status: value })}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Quick Actions */}
                  {merchant.subscription_status !== 'trialing' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startTrial(merchant.id)}
                      disabled={isUpdating}
                    >
                      {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Trial'}
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => resetSplitCount(merchant.id)}
                    disabled={isUpdating}
                    title="Reset split count & start new billing cycle"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {merchants.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-center text-muted-foreground">
            No merchants found
          </CardContent>
        </Card>
      )}
    </div>
  );
};
